import { SendHorizontal } from "lucide-react";
import { useState } from "react";
import { StarInput } from "@/components/skills/detail/StarInput";
import { Stars } from "@/components/skills/Stars";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import type { SkillReview } from "@/hooks/use-skill";
import { usePostReview } from "@/hooks/use-skill-mutations";
import { initials } from "@/lib/skills/format";
import { timeAgo } from "@/lib/skills/time-ago";

export function ReviewsSection({ slug, reviews }: { slug: string; reviews: SkillReview[] }) {
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const post = usePostReview(slug);

  function submit() {
    if (!rating || !text.trim()) return;
    post.mutate(
      { rating, text: text.trim() },
      {
        onSuccess: () => {
          setRating(0);
          setText("");
        },
      },
    );
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 rounded-[12px] border border-border-1 bg-white p-5">
        <div className="text-sm font-semibold text-fg-1">Rate &amp; review this skill</div>
        <StarInput value={rating} onChange={setRating} />
        <Textarea
          placeholder="Where did this skill work well? Any limitations to flag?"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
        />
        <div className="flex justify-end">
          <Button onClick={submit} disabled={!rating || !text.trim() || post.isPending}>
            <SendHorizontal className="size-4" />
            Post review
          </Button>
        </div>
      </div>

      {reviews.length === 0 ? (
        <p className="text-sm text-fg-3">No reviews yet — be the first.</p>
      ) : (
        reviews.map((rv) => (
          <div
            key={rv.id}
            className="flex gap-3 rounded-[12px] border border-border-1 bg-white p-4"
          >
            <span className="grid size-9 shrink-0 place-items-center rounded-full bg-bg-2 text-xs font-semibold text-fg-link">
              {initials(rv.userName)}
            </span>
            <div className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-semibold text-fg-1">{rv.userName}</span>
                {rv.userTeam ? <span className="text-fg-3">· {rv.userTeam}</span> : null}
                <Stars value={rv.rating} />
                <span className="text-xs text-fg-3">{timeAgo(new Date(rv.createdAt))}</span>
              </div>
              <p className="text-sm text-fg-2">{rv.text}</p>
            </div>
          </div>
        ))
      )}
    </section>
  );
}
